import { useMemo } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { useCurrentBoardIdSelector } from '~/hooks/useCurrentBoardIdSelector';
import {
  type BoardChildBoardsFetcherParams,
  type BoardChildFetchers,
  useBoardChildGalleryViewItems,
} from '~/swr-hooks/gallery/galleryView/useBoardChildGalleryViewItems';
import { type ClipsFetcherParams } from '~/swr-hooks/gallery/galleryView/useCollectionGalleryViewItems';
import { usePublicBoardsFetcher } from '~/swr-hooks/gallery/galleryView/usePublicBoardsFetcher';
import { publicClipsFetcher, publicFilesFetcher } from '~/swr-hooks/gallery/galleryView/utils';
import { defaultAssetsData, defaultBoardsData } from '~/swr-hooks/gallery/types';

export type UsePublicGalleryViewItemsProps = {
  shouldUpdateMediaPlayer?: boolean;
  /**
   * The collection the board is being viewed within, so every fetch scopes to that collection —
   * collection surfaces always send it (the active sub-page's collection, or the link's root),
   * never leaning on the backend's root-collection default. Absent ONLY on the standalone board
   * link, which has no collection; its requests stay byte-identical to the pre-sub-pages shapes.
   */
  collectionId?: string;
};

export const usePublicGalleryViewItems = ({
  shouldUpdateMediaPlayer = true,
  collectionId,
}: UsePublicGalleryViewItemsProps = {}) => {
  const { publicBoardsFetcher } = usePublicBoardsFetcher();
  const currentBoardId = useCurrentBoardIdSelector();
  const { shortId } = useShortIdContext();

  const fetchers = useMemo<BoardChildFetchers<Board, Clip>>(
    () => ({
      boardsFetcher: async ({
        cursor,
        sortFieldName,
        sortDirection,
        boardsFetchParams,
      }: BoardChildBoardsFetcherParams) => {
        if (!currentBoardId) {
          return defaultBoardsData;
        }

        return publicBoardsFetcher({
          shortId,
          parentId: currentBoardId,
          params: boardsFetchParams,
          sortFieldName,
          sortDirection,
          cursor,
          collectionId,
        });
      },
      clipsFetcher: async ({ cursor, sortFieldName, sortDirection, clipsFetchParams }: ClipsFetcherParams) => {
        if (!currentBoardId) {
          return defaultAssetsData;
        }

        return publicClipsFetcher({
          shortId,
          params: clipsFetchParams,
          sortFieldName,
          sortDirection,
          cursor,
          parentId: currentBoardId,
          collectionId,
        });
      },
      filesFetcher: async ({ cursor, sortFieldName, sortDirection, clipsFetchParams }: ClipsFetcherParams) => {
        if (!currentBoardId) {
          return defaultAssetsData;
        }

        return publicFilesFetcher({
          shortId,
          params: clipsFetchParams,
          sortFieldName,
          sortDirection,
          parentId: currentBoardId,
          cursor,
          collectionId,
        });
      },
    }),
    [collectionId, currentBoardId, publicBoardsFetcher, shortId],
  );

  return useBoardChildGalleryViewItems({
    fetchers,
    // Spread in only when present so the standalone board link keeps its pre-sub-pages cache keys,
    // while collection drills — scoped to root or sub-page — never share entries across scopes.
    cacheKeyParams: collectionId ? { collectionId } : {},
    shouldUpdateMediaPlayer,
  });
};
